import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Wordmark } from "../components/Wordmark";
import { ApiError, api } from "../lib/api";
import { Icon } from "../lib/icons";
import { renderMarkdown } from "../lib/markdown";

// Ask the inbox a question — surfaces the read-only `query` synthesis
// (calendar + reminders/tasks + captures) that the Telegram bot already
// answers. Read-only: nothing is recorded.
export function Ask() {
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");

  const mutation = useMutation({ mutationFn: (q: string) => api.ask(q) });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (q) mutation.mutate(q);
  };

  // Fail loud: surface the failure rather than leaving the box silent.
  const errorMsg =
    mutation.error instanceof ApiError
      ? `Couldn't answer that (${mutation.error.status}).`
      : mutation.error
        ? "Couldn't answer — check your connection and try again."
        : null;

  return (
    <>
      <header className="header">
        <div className="col">
          <div className="header-inner">
            <button className="back-link" onClick={() => navigate("/")}>
              <Icon.back /> timeline
            </button>
            <div style={{ flex: 1 }} />
            <Wordmark />
          </div>
        </div>
      </header>

      <main className="col ask">
        <form className="ask-form" onSubmit={submit}>
          <input
            className="ask-input"
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about your calendar, reminders, or notes…"
            autoFocus
          />
          <button
            className="ask-submit"
            type="submit"
            disabled={mutation.isPending || !question.trim()}
          >
            {mutation.isPending ? "Thinking…" : "Ask"}
          </button>
        </form>

        {errorMsg ? (
          <p className="login-error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        {mutation.data ? (
          <article className="ask-answer fade-up">
            <div className="note-body">
              {renderMarkdown(mutation.data.answer || "(no answer)")}
            </div>
            {mutation.data.sources?.length ? (
              <div className="ask-sources">
                <p className="detail-section-label">Sources</p>
                <ul>
                  {mutation.data.sources.map((s) => (
                    <li key={s.id}>
                      <Link to={`/c/${s.id}`}>
                        ({s.category}) {s.summary}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </article>
        ) : null}
      </main>
    </>
  );
}
